import { useEffect, useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { monthHeading } from '../record/format'
import { CHIPS, type ChipKey } from './chips'
import { TimelineItem } from './TimelineItem'
import type { RelationRef, TimelineItem as TimelineItemData } from './types'
import { itemKey, useTimeline } from './useTimeline'

const PAGE_SIZE = 30
const SEARCH_PAUSE_MS = 300
// The API searches from two characters; below that the box is only a half-typed word.
const MIN_SEARCH = 2

interface MonthGroup {
  key: string
  heading: string
  items: TimelineItemData[]
}

/** The items are newest first, so the months follow each other: group the neighbours. */
function groupByMonth(items: TimelineItemData[]): MonthGroup[] {
  const groups: MonthGroup[] = []
  for (const item of items) {
    const key = item.day.slice(0, 7)
    const last = groups.at(-1)
    if (last && last.key === key) last.items.push(item)
    else groups.push({ key, heading: monthHeading(item.day), items: [item] })
  }
  return groups
}

/** "Activiteit": the one timeline of a relation (design §4.5), with Gepland on top of the lines. */
export function Timeline({
  relation,
  typeKey,
  onTypeKeyChange,
  includeMembers,
  onIncludeMembersChange,
  version,
  planned,
  onChanged,
}: {
  relation: RelationRef
  /** The chip in the URL (`?type=`); none is Alles. */
  typeKey: ChipKey | null
  onTypeKeyChange: (key: ChipKey | null) => void
  /** Organisation page: whether the current contact persons' lines come along (default on). */
  includeMembers?: boolean
  onIncludeMembersChange?: (include: boolean) => void
  /** Bump to load the first page again (something was saved elsewhere on the page). */
  version: number
  /** Gepland, between the chips and the lines. */
  planned?: ReactNode
  /** An activity was edited, deleted or restored. */
  onChanged: () => void
}) {
  const members = includeMembers ?? true
  const chip = CHIPS.find((entry) => entry.key === typeKey) ?? CHIPS[0]
  const [searchText, setSearchText] = useState('')
  const [q, setQ] = useState('')
  const [expandAll, setExpandAll] = useState(false)

  // The search waits for a pause in the typing.
  useEffect(() => {
    const term = searchText.trim()
    const handle = window.setTimeout(() => setQ(term.length >= MIN_SEARCH ? term : ''), SEARCH_PAUSE_MS)
    return () => window.clearTimeout(handle)
  }, [searchText])

  const timeline = useTimeline(relation, {
    types: typeKey ? chip.types : null,
    q,
    includeMembers: members,
    limit: PAGE_SIZE,
    version,
  })
  const { items, counts, status } = timeline
  const groups = groupByMonth(items)

  return (
    <section aria-label="Activiteit" className="vonk-card flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          aria-label="Zoek in activiteit"
          placeholder="Zoek in activiteit…"
          maxLength={200}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          className="min-w-48 flex-1 max-md:h-11 max-md:text-base"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-pressed={expandAll}
          onClick={() => setExpandAll((current) => !current)}
        >
          {expandAll ? 'Alles inklappen' : 'Alles uitklappen'}
        </Button>
        {relation.kind === 'organisation' ? (
          <label className="flex items-center gap-2 text-sm max-md:min-h-11">
            <input
              type="checkbox"
              checked={members}
              onChange={(event) => onIncludeMembersChange?.(event.target.checked)}
              className="size-4"
            />
            Inclusief contactpersonen
          </label>
        ) : null}
      </div>

      <div role="group" aria-label="Filter op type" className="flex flex-wrap gap-1.5">
        {CHIPS.map((entry) => {
          const pressed = entry.key === typeKey
          const count = counts ? entry.types.reduce((sum, type) => sum + counts[type], 0) : null
          return (
            <button
              key={entry.key ?? 'alles'}
              type="button"
              aria-pressed={pressed}
              onClick={() => onTypeKeyChange(entry.key)}
              className={cn(
                'rounded-full border px-3 py-1 text-sm font-medium max-md:min-h-11',
                pressed
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {count === null ? entry.label : `${entry.label} ${count}`}
            </button>
          )
        })}
      </div>

      {planned}

      {status === 'loading' ? (
        <div aria-busy="true" className="flex flex-col gap-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : status === 'error' ? (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-muted-foreground">Activiteit laden mislukt.</p>
          <Button type="button" variant="outline" size="sm" onClick={timeline.reload}>
            Opnieuw proberen
          </Button>
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{q ? `Niets gevonden voor '${q}'.` : 'Nog geen activiteit.'}</p>
      ) : (
        <div className="flex flex-col gap-5">
          {groups.map((group) => (
            <div key={group.key} className="flex flex-col gap-3">
              <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{group.heading}</h3>
              <ol className="flex flex-col gap-4">
                {group.items.map((item) => (
                  <TimelineItem
                    key={itemKey(item)}
                    item={item}
                    relation={relation}
                    expandAll={expandAll}
                    includeMembers={members}
                    onReplace={timeline.replace}
                    onRemove={timeline.remove}
                    onReload={timeline.reload}
                    onChanged={onChanged}
                  />
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}

      <p aria-live="polite" className="sr-only">
        {status === 'ready' ? `${items.length} ${items.length === 1 ? 'regel' : 'regels'} getoond` : ''}
      </p>

      {timeline.nextCursor ? (
        <Button
          type="button"
          variant="outline"
          className="self-start"
          disabled={timeline.loadingMore}
          onClick={timeline.loadMore}
        >
          {timeline.loadingMore ? 'Laden…' : 'Meer laden'}
        </Button>
      ) : null}
    </section>
  )
}
